import { useEffect, useState } from 'react';
import { authService } from '@/services/authService';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RefreshCw } from 'lucide-react';

interface ClaimsDebugResponse {
  isAuthenticated: boolean;
  authenticationType: string;
  name: string;
  claims: Array<{ type: string; value: string }>;
  isInAdminRole: boolean;
  isInUserRole: boolean;
  isInManagerRole: boolean;
}

export default function DebugClaims() {
  const [claims, setClaims] = useState<ClaimsDebugResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchClaims = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.getClaims();
      setClaims(response.data);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      setError(error.response?.data?.message || error.message || 'Failed to fetch claims');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClaims();
  }, []);

  return (
    <div className="container mx-auto p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Debug: Token Claims</h1>
        <Button onClick={fetchClaims} disabled={loading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      {claims && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Authentication Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="font-medium">Authenticated:</span>
                <Badge variant={claims.isAuthenticated ? 'default' : 'destructive'}>
                  {claims.isAuthenticated ? 'Yes' : 'No'}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium">Auth Type:</span>
                <span>{claims.authenticationType}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium">Name:</span>
                <span>{claims.name}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Role Checks</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="font-medium">Is Admin:</span>
                <Badge variant={claims.isInAdminRole ? 'default' : 'outline'}>
                  {claims.isInAdminRole ? 'Yes' : 'No'}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium">Is User:</span>
                <Badge variant={claims.isInUserRole ? 'default' : 'outline'}>
                  {claims.isInUserRole ? 'Yes' : 'No'}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium">Is Manager:</span>
                <Badge variant={claims.isInManagerRole ? 'default' : 'outline'}>
                  {claims.isInManagerRole ? 'Yes' : 'No'}
                </Badge>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>All Claims ({claims.claims.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {claims.claims.map((claim, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-2 p-2 border rounded text-sm"
                  >
                    <Badge variant="outline" className="shrink-0">
                      {claim.type.split('/').pop()}
                    </Badge>
                    <code className="flex-1 text-xs break-all">{claim.value}</code>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
